import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  useScrollTrigger,
  Chip,
  Divider,
} from '@mui/material';
import { Container } from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Person,
  Dashboard,
  Logout,
  Radio,
  Headphones,
  Login as LoginIcon,
  CalendarMonth,
  Event as EventIcon,
  Groups,
  Forum,
  KeyboardArrowDown,
  Home as HomeIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import ProgrammingModal from '../modals/ProgrammingModal';

// Frases que alternam no Header com efeito de mutação líquida
const BRAND_PHRASES = [
  {
    id: 'brand',
    text: 'NostalBeat',
    showLiveTag: true,
    fontSize: { xs: '1.18rem', sm: '1.28rem', md: '1.45rem' },
    letterSpacing: '0.04em',
  },
  {
    id: 'slogan1',
    text: 'Sinta a batida da nostalgia',
    showLiveTag: false,
    fontSize: { xs: '0.74rem', sm: '0.86rem', md: '1.02rem', lg: '1.12rem' },
    letterSpacing: { xs: '0.01em', md: '0.02em' },
  },
  {
    id: 'slogan2',
    text: '24 horas com você!',
    showLiveTag: false,
    fontSize: { xs: '0.85rem', sm: '0.98rem', md: '1.15rem', lg: '1.24rem' },
    letterSpacing: '0.03em',
  },
];

const Header = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const scrolled = useScrollTrigger({
    disableHysteresis: true,
    threshold: 20,
  });

  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [programmingModalOpen, setProgrammingModalOpen] = useState(false);

  // Controle de alternância das frases a cada 10s com efeito de derretimento
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [animState, setAnimState] = useState('idle'); // 'idle' | 'melting' | 'dripping'

  useEffect(() => {
    const timer = setInterval(() => {
      // 1. Inicia o derretimento do texto atual
      setAnimState('melting');

      // 2. Após derreter (600ms), troca a frase e entra escorrendo
      setTimeout(() => {
        setPhraseIndex((prev) => (prev + 1) % BRAND_PHRASES.length);
        setAnimState('dripping');

        // 3. Volta ao repouso após 600ms
        setTimeout(() => {
          setAnimState('idle');
        }, 600);
      }, 600);
    }, 10000); // Exatamente a cada 10 segundos

    return () => clearInterval(timer);
  }, []);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
    navigate('/');
  };

  const handleNavigation = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const smoothScrollTo = (element) => {
    if (!element) return;
    const headerOffset = 80;
    const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementPosition - headerOffset;

    window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
  };

  const goHomeAndScroll = (getTarget) => {
    const doScroll = () => {
      const el = getTarget();
      if (el) smoothScrollTo(el);
    };
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(doScroll, 150);
    } else {
      doScroll();
    }
  };

  const menuItems = [
    { label: 'Início', action: 'home', icon: <HomeIcon fontSize="small" /> },
    { label: 'Programação', action: 'programming', icon: <CalendarMonth fontSize="small" /> },
    { label: 'Eventos', action: 'events', icon: <EventIcon fontSize="small" /> },
    { label: 'Equipe', action: 'team', icon: <Groups fontSize="small" /> },
    { label: 'Chat', action: 'chat', icon: <Forum fontSize="small" /> },
  ];

  const handleMenuAction = (action) => {
    switch (action) {
      case 'home':
        if (location.pathname !== '/') {
          navigate('/');
          setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }, 150);
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        break;
      case 'programming':
        setProgrammingModalOpen(true);
        break;
      case 'events':
        goHomeAndScroll(() => document.getElementById('events-section') || document.querySelector('[data-section="events"]'));
        break;
      case 'team':
        goHomeAndScroll(() => document.getElementById('team-birthday-section'));
        break;
      case 'chat':
        goHomeAndScroll(() => document.getElementById('chat-section'));
        break;
      default:
        break;
    }
    setMobileMenuOpen(false);
  };

  const renderDesktopMenu = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {/* Navigation Links */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          background: 'rgba(255, 255, 255, 0.03)',
          border: '1px solid rgba(255, 255, 255, 0.06)',
          borderRadius: '50px',
          px: 1,
          py: 0.5,
          backdropFilter: 'blur(8px)',
        }}
      >
        {menuItems.map((item) => (
          <Button
            key={item.label}
            onClick={() => handleMenuAction(item.action)}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              color: 'rgba(255, 255, 255, 0.85)',
              px: 1.8,
              py: 0.7,
              borderRadius: '50px',
              transition: 'all 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
              position: 'relative',
              '&:hover': {
                color: '#FFFFFF',
                background: 'rgba(255, 255, 255, 0.08)',
                transform: 'translateY(-1px)',
                '& .nav-indicator': {
                  opacity: 1,
                  transform: 'scaleX(1)',
                },
              },
            }}
          >
            {item.label}
            <Box
              className="nav-indicator"
              sx={{
                position: 'absolute',
                bottom: 4,
                left: '25%',
                width: '50%',
                height: '2px',
                borderRadius: '2px',
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                opacity: 0,
                transform: 'scaleX(0)',
                transition: 'all 0.25s ease',
              }}
            />
          </Button>
        ))}
      </Box>

      {/* Auth & Actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 1.5 }}>
        {isAuthenticated ? (
          <>
            <Button
              onClick={() => handleNavigation('/dashboard')}
              startIcon={<Dashboard sx={{ color: theme.palette.primary.main }} />}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.875rem',
                color: '#FFFFFF',
                background: 'rgba(255, 255, 255, 0.04)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                borderRadius: '50px',
                px: 2,
                py: 0.7,
                transition: 'all 0.25s ease',
                '&:hover': {
                  background: 'rgba(255, 107, 53, 0.12)',
                  borderColor: theme.palette.primary.main,
                  transform: 'translateY(-1px)',
                },
              }}
            >
              Dashboard
            </Button>
            
            <Box
              onClick={handleMenuOpen}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                p: 0.5,
                pr: 1.5,
                background: 'rgba(255, 255, 255, 0.04)',
                border: '1px solid rgba(255, 107, 53, 0.3)',
                borderRadius: '50px',
                transition: 'all 0.25s ease',
                '&:hover': {
                  background: 'rgba(255, 107, 53, 0.15)',
                  borderColor: theme.palette.primary.main,
                  boxShadow: `0 0 15px ${theme.palette.primary.main}40`,
                },
              }}
            >
              <Avatar
                src={user?.profile_image}
                sx={{
                  width: 34,
                  height: 34,
                  bgcolor: theme.palette.primary.main,
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  boxShadow: `0 0 10px ${theme.palette.primary.main}60`,
                }}
              >
                {user?.name?.charAt(0)}
              </Avatar>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: '#FFFFFF',
                  maxWidth: 120,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {user?.name}
              </Typography>
              <KeyboardArrowDown sx={{ fontSize: 18, color: 'rgba(255, 255, 255, 0.6)' }} />
            </Box>
            
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              PaperProps={{
                sx: {
                  mt: 1.5,
                  bgcolor: 'rgba(14, 14, 20, 0.95)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: 3,
                  boxShadow: '0 12px 30px rgba(0, 0, 0, 0.6), 0 0 20px rgba(255, 107, 53, 0.15)',
                  minWidth: 190,
                  p: 0.5,
                }
              }}
            >
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#FFFFFF' }}>
                  {user?.name}
                </Typography>
                <Typography variant="caption" sx={{ color: theme.palette.secondary.main }}>
                  DJ NostalBeat
                </Typography>
              </Box>
              <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)', my: 0.5 }} />
              <MenuItem
                onClick={() => { handleMenuClose(); handleNavigation('/dashboard'); }}
                sx={{
                  borderRadius: 2,
                  py: 1,
                  '&:hover': { bgcolor: 'rgba(255, 107, 53, 0.15)', color: theme.palette.primary.light },
                }}
              >
                <ListItemIcon>
                  <Person sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                </ListItemIcon>
                <ListItemText primary="Meu Perfil" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 600 }} />
              </MenuItem>
              <MenuItem
                onClick={handleLogout}
                sx={{
                  borderRadius: 2,
                  py: 1,
                  color: theme.palette.error.light,
                  '&:hover': { bgcolor: 'rgba(255, 23, 68, 0.15)' },
                }}
              >
                <ListItemIcon>
                  <Logout sx={{ color: theme.palette.error.main, fontSize: 20 }} />
                </ListItemIcon>
                <ListItemText primary="Sair da Conta" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 600 }} />
              </MenuItem>
            </Menu>
          </>
        ) : (
          <>
            <Button
              onClick={() => handleNavigation('/login')}
              startIcon={<LoginIcon sx={{ fontSize: 18, color: theme.palette.secondary.main }} />}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.875rem',
                color: '#FFFFFF',
                background: 'rgba(255, 255, 255, 0.04)',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                borderRadius: '50px',
                px: 2.2,
                py: 0.8,
                transition: 'all 0.25s ease',
                '&:hover': {
                  background: 'rgba(0, 212, 255, 0.08)',
                  borderColor: theme.palette.secondary.main,
                  boxShadow: `0 0 15px ${theme.palette.secondary.main}30`,
                  transform: 'translateY(-1px)',
                },
              }}
            >
              Login DJ
            </Button>

            <Button
              variant="contained"
              onClick={() => handleNavigation('/register')}
              startIcon={<Headphones sx={{ fontSize: 18 }} />}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.875rem',
                borderRadius: '50px',
                px: 2.8,
                py: 0.85,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #FF8C5A 100%)`,
                boxShadow: `0 4px 18px ${theme.palette.primary.main}50`,
                transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, #FFA502 100%)`,
                  boxShadow: `0 6px 24px ${theme.palette.primary.main}80`,
                  transform: 'translateY(-2px) scale(1.02)',
                },
              }}
            >
              Quero Tocar
            </Button>
          </>
        )}
      </Box>
    </Box>
  );

  const renderMobileMenu = () => (
    <>
      <IconButton
        onClick={() => setMobileMenuOpen(true)}
        sx={{
          color: '#FFFFFF',
          background: 'rgba(255, 255, 255, 0.06)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 2.5,
          p: 1,
          '&:hover': {
            background: `${theme.palette.primary.main}20`,
            borderColor: theme.palette.primary.main,
          },
        }}
      >
        <MenuIcon />
      </IconButton>
      
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: 'rgba(10, 10, 14, 0.96)',
            backdropFilter: 'blur(25px)',
            borderLeft: '1px solid rgba(255, 255, 255, 0.08)',
            width: 300,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            p: 2.5,
          }
        }}
      >
        <Box>
          {/* Drawer Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 36,
                  height: 36,
                  borderRadius: '10px',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}25, ${theme.palette.secondary.main}25)`,
                  border: `1px solid ${theme.palette.primary.main}50`,
                }}
              >
                <Radio sx={{ fontSize: 20, color: theme.palette.primary.main }} />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: 'Orbitron',
                  fontWeight: 900,
                  fontSize: '1.1rem',
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                NostalBeat
              </Typography>
            </Box>
            
            <IconButton
              onClick={() => setMobileMenuOpen(false)}
              size="small"
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                background: 'rgba(255, 255, 255, 0.05)',
                '&:hover': { color: '#FFFFFF', background: 'rgba(255, 255, 255, 0.1)' }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)', mb: 2 }} />

          {/* Nav List */}
          <List sx={{ p: 0 }}>
            {menuItems.map((item) => (
              <ListItem
                key={item.label}
                button
                onClick={() => handleMenuAction(item.action)}
                sx={{
                  borderRadius: 2.5,
                  mb: 0.8,
                  py: 1.2,
                  px: 2,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: `${theme.palette.primary.main}1A`,
                    transform: 'translateX(4px)',
                    '& .MuiListItemIcon-root': {
                      color: theme.palette.primary.main,
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 38, color: 'rgba(255, 255, 255, 0.7)' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    color: '#FFFFFF',
                  }}
                />
              </ListItem>
            ))}
            
            {isAuthenticated && (
              <>
                <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)', my: 1.5 }} />
                <ListItem
                  button
                  onClick={() => handleNavigation('/dashboard')}
                  sx={{
                    borderRadius: 2.5,
                    mb: 0.8,
                    py: 1.2,
                    px: 2,
                    bgcolor: 'rgba(255, 107, 53, 0.1)',
                    border: `1px solid ${theme.palette.primary.main}30`,
                    '&:hover': {
                      bgcolor: 'rgba(255, 107, 53, 0.2)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 38, color: theme.palette.primary.main }}>
                    <Dashboard />
                  </ListItemIcon>
                  <ListItemText
                    primary="Painel do DJ"
                    primaryTypographyProps={{
                      fontWeight: 700,
                      fontSize: '0.95rem',
                      color: theme.palette.primary.light,
                    }}
                  />
                </ListItem>
              </>
            )}
          </List>
        </Box>

        {/* Drawer Footer Actions */}
        <Box sx={{ pt: 2, borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
          {isAuthenticated ? (
            <Button
              fullWidth
              variant="outlined"
              color="error"
              onClick={handleLogout}
              startIcon={<Logout />}
              sx={{
                borderRadius: 3,
                py: 1.2,
                textTransform: 'none',
                fontWeight: 600,
                borderColor: 'rgba(255, 23, 68, 0.4)',
              }}
            >
              Sair da Conta
            </Button>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={() => handleNavigation('/register')}
                startIcon={<Headphones />}
                sx={{
                  py: 1.3,
                  borderRadius: 3,
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #FF8C5A 100%)`,
                  boxShadow: `0 4px 18px ${theme.palette.primary.main}50`,
                }}
              >
                Quero Tocar
              </Button>

              <Button
                fullWidth
                variant="outlined"
                onClick={() => handleNavigation('/login')}
                startIcon={<LoginIcon />}
                sx={{
                  py: 1.2,
                  borderRadius: 3,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  color: '#FFFFFF',
                  borderColor: 'rgba(255, 255, 255, 0.15)',
                  background: 'rgba(255, 255, 255, 0.03)',
                  '&:hover': {
                    borderColor: theme.palette.secondary.main,
                    bgcolor: 'rgba(0, 212, 255, 0.08)',
                  },
                }}
              >
                Login DJ
              </Button>
            </Box>
          )}
        </Box>
      </Drawer>
    </>
  );

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        zIndex: 1300,
        background: {
          xs: scrolled ? 'rgba(10, 10, 14, 0.97)' : 'rgba(10, 10, 14, 0.90)',
          md: scrolled ? 'rgba(10, 10, 14, 0.88)' : 'rgba(10, 10, 14, 0.65)',
        },
        backdropFilter: { xs: 'none', md: 'blur(20px) saturate(180%)' },
        WebkitBackdropFilter: { xs: 'none', md: 'blur(20px) saturate(180%)' },
        borderBottom: scrolled
          ? `1px solid rgba(255, 107, 53, 0.2)`
          : '1px solid rgba(255, 255, 255, 0.06)',
        boxShadow: scrolled
          ? '0 8px 30px rgba(0, 0, 0, 0.6), 0 1px 0 rgba(255, 107, 53, 0.15)'
          : 'none',
        transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
        py: scrolled ? 0.3 : 0.8,
      }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ minHeight: { xs: 60, md: 68 } }}>
          {/* Brand / Logo */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.2,
              cursor: 'pointer',
              userSelect: 'none',
              transition: 'transform 0.25s ease',
              '&:hover': {
                transform: 'scale(1.02)',
              },
            }}
            onClick={() => handleNavigation('/')}
          >
            {/* Glowing Icon Container */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 38,
                height: 38,
                borderRadius: '12px',
                background: `linear-gradient(135deg, ${theme.palette.primary.main}20 0%, ${theme.palette.secondary.main}20 100%)`,
                border: `1px solid ${theme.palette.primary.main}40`,
                boxShadow: `0 0 16px ${theme.palette.primary.main}35`,
                position: 'relative',
              }}
            >
              <Radio
                sx={{
                  color: theme.palette.primary.main,
                  fontSize: 22,
                  filter: `drop-shadow(0 0 6px ${theme.palette.primary.main})`,
                }}
              />
              {/* Pulsing Live Dot */}
              <Box
                sx={{
                  position: 'absolute',
                  top: -2,
                  right: -2,
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: theme.palette.secondary.main,
                  boxShadow: `0 0 8px ${theme.palette.secondary.main}`,
                  '@keyframes pulseDot': {
                    '0%': { transform: 'scale(0.95)', opacity: 0.8 },
                    '50%': { transform: 'scale(1.3)', opacity: 1 },
                    '100%': { transform: 'scale(0.95)', opacity: 0.8 },
                  },
                  animation: 'pulseDot 2s infinite ease-in-out',
                }}
              />
            </Box>

            {/* Logo Text & Liquid Mutation Container */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                minWidth: { xs: '140px', sm: '200px', md: '260px' },
                maxWidth: { xs: 'calc(100vw - 120px)', sm: '270px', md: '340px' },
                height: 38,
                overflow: 'hidden',
                position: 'relative',
                '@keyframes liquidMeltOut': {
                  '0%': {
                    opacity: 1,
                    transform: 'translateY(0) scaleY(1)',
                    filter: 'blur(0px) drop-shadow(0 2px 10px rgba(255, 107, 53, 0.3))',
                  },
                  '35%': {
                    opacity: 0.85,
                    transform: 'translateY(8px) scaleY(1.35)',
                    filter: 'blur(2px) drop-shadow(0 6px 12px rgba(255, 107, 53, 0.8))',
                  },
                  '100%': {
                    opacity: 0,
                    transform: 'translateY(26px) scaleY(1.75)',
                    filter: 'blur(7px) drop-shadow(0 14px 18px rgba(0, 212, 255, 0.9))',
                  },
                },
                '@keyframes liquidDripIn': {
                  '0%': {
                    opacity: 0,
                    transform: 'translateY(-24px) scaleY(1.65)',
                    filter: 'blur(7px) drop-shadow(0 -6px 16px rgba(0, 212, 255, 0.9))',
                  },
                  '55%': {
                    opacity: 0.95,
                    transform: 'translateY(3px) scaleY(0.95)',
                    filter: 'blur(1px) drop-shadow(0 0 12px rgba(255, 107, 53, 0.8))',
                  },
                  '100%': {
                    opacity: 1,
                    transform: 'translateY(0) scaleY(1)',
                    filter: 'blur(0px) drop-shadow(0 2px 10px rgba(255, 107, 53, 0.3))',
                  },
                },
              }}
            >
              <Box
                key={phraseIndex}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.8,
                  whiteSpace: 'nowrap',
                  animation:
                    animState === 'melting'
                      ? 'liquidMeltOut 0.6s cubic-bezier(0.55, 0.055, 0.675, 0.19) forwards'
                      : animState === 'dripping'
                        ? 'liquidDripIn 0.6s cubic-bezier(0.215, 0.61, 0.355, 1) forwards'
                        : 'none',
                }}
              >
                <Typography
                  variant="h5"
                  component="div"
                  sx={{
                    fontFamily: 'Orbitron',
                    fontWeight: 900,
                    fontSize: BRAND_PHRASES[phraseIndex].fontSize,
                    letterSpacing: BRAND_PHRASES[phraseIndex].letterSpacing,
                    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, #FF8C5A 50%, ${theme.palette.secondary.main} 100%)`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    filter: `drop-shadow(0 2px 10px ${theme.palette.primary.main}30)`,
                    whiteSpace: 'nowrap',
                    lineHeight: 1.1,
                    transition: 'font-size 0.2s ease',
                  }}
                >
                  {BRAND_PHRASES[phraseIndex].text}
                </Typography>

                {BRAND_PHRASES[phraseIndex].showLiveTag && (
                  <Chip
                    label="LIVE"
                    size="small"
                    sx={{
                      display: { xs: 'none', sm: 'inline-flex' },
                      height: 18,
                      fontSize: '0.65rem',
                      fontWeight: 900,
                      fontFamily: 'Orbitron',
                      bgcolor: 'rgba(0, 212, 255, 0.12)',
                      color: theme.palette.secondary.main,
                      border: `1px solid ${theme.palette.secondary.main}40`,
                      px: 0.2,
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>
          
          <Box sx={{ flexGrow: 1 }} />
          
          {isMobile ? renderMobileMenu() : renderDesktopMenu()}
        </Toolbar>
      </Container>
      
      {/* Programming Modal */}
      <ProgrammingModal
        open={programmingModalOpen}
        onClose={() => setProgrammingModalOpen(false)}
      />
    </AppBar>
  );
};

export default Header;
